import { useEffect, useState, useRef } from "react";
import { X, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";

const TOUR_STEPS = [
  {
    selector: '[data-testid="aria-orb"]',
    title: "Meet your ARIA orb",
    body: "She breathes when idle, pulses when listening, and spins when thinking. Watch her come alive as you chat.",
    placement: "right",
    path: "/app/chat",
  },
  {
    selector: '[data-testid="chat-input"]',
    title: "Ask anything from your connected knowledge",
    body: "Type or paste a question — ARIA searches every connected lecture, note, TurboLearn import, and paper, then answers with citations.",
    placement: "top",
    path: "/app/chat",
  },
  {
    selector: '[data-testid="mic-button"]',
    title: "Talk to her hands-free",
    body: "Tap the mic to dictate. ARIA transcribes via ElevenLabs and reads answers back when Voice mode is on.",
    placement: "top",
    path: "/app/chat",
  },
  {
    selector: '[data-testid="nav-notes"]',
    title: "Bring in your notes",
    body: "Type, paste, or upload notes — ARIA extracts concepts and links them into your knowledge graph automatically.",
    placement: "right",
    path: "/app/chat",
  },
  {
    selector: '[data-testid="nav-interviews"]',
    title: "Practice under pressure",
    body: "Adaptive mock interviews probe your weak spots, teach the gap on the spot, and update your mastery from real evidence.",
    placement: "right",
    path: "/app/chat",
  },
  {
    selector: '[data-testid="nav-knowledge"]',
    title: "Watch it all connect",
    body: "See every concept you've touched, its mastery level, and how it links to everything else you've learned.",
    placement: "right",
    path: "/app/chat",
  },
];

const KEY = "aria_tour_seen_v1";

export default function GuidedTour() {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [pos, setPos] = useState(null);
  const overlayRef = useRef(null);

  // The tour is explicitly launched, never injected over an upload or chat interaction.

  // Recompute target rect when step/idx changes — never navigates; if the
  // step's element isn't on the current page, the popover just centers.
  useEffect(() => {
    if (!open) return;
    const step = TOUR_STEPS[idx];
    const update = () => {
      const el = document.querySelector(step.selector);
      if (!el) { setPos(null); return; }
      const r = el.getBoundingClientRect();
      setPos({ top: r.top, left: r.left, width: r.width, height: r.height });
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    update();
    const i = setInterval(update, 300);
    window.addEventListener("resize", update);
    return () => { clearInterval(i); window.removeEventListener("resize", update); };
  }, [open, idx]);

  const finish = (skip = false) => {
    localStorage.setItem(KEY, "1");
    setOpen(false); setIdx(0);
  };

  const next = () => {
    if (idx >= TOUR_STEPS.length - 1) finish();
    else setIdx(i => i + 1);
  };
  const prev = () => setIdx(i => Math.max(0, i - 1));

  if (!open) {
    return (
      <button
        onClick={() => { localStorage.setItem(KEY, "1"); setIdx(0); setOpen(true); }}
        data-testid="start-tour-btn"
        title="Take a tour"
        className="fixed bottom-6 right-24 z-30 flex items-center gap-2 px-3 py-2 rounded-full border border-[var(--aria-border-strong)] bg-[var(--aria-surface)] text-[var(--aria-text)] text-xs shadow-lg hover:bg-[var(--aria-surface-2)] transition-colors"
      >
        <Sparkles size={14} /> Take the tour
      </button>
    );
  }

  const step = TOUR_STEPS[idx];

  // Place tooltip
  const TW = Math.min(340, window.innerWidth - 24), TH = 250;
  let style = { position: "fixed", maxWidth: TW, width: TW, zIndex: 10001 };
  if (pos) {
    const pad = 16;
    if (step.placement === "right") {
      style.top = pos.top + pos.height / 2 - 80;
      style.left = pos.left + pos.width + pad;
      // if it would overflow the right edge, flip to the left of the target
      if (style.left + TW > window.innerWidth - 12) style.left = pos.left - TW - pad;
    } else if (step.placement === "top") {
      style.top = pos.top - TH - pad;
      style.left = pos.left + pos.width / 2 - TW / 2;
    } else if (step.placement === "bottom") {
      style.top = pos.top + pos.height + pad;
      style.left = pos.left + pos.width / 2 - TW / 2;
    } else {
      style.top = pos.top + pos.height + pad;
      style.left = pos.left;
    }
    // Clamp fully inside the viewport on both axes so it's never cut off.
    style.left = Math.min(Math.max(12, style.left), window.innerWidth - TW - 12);
    style.top = Math.min(Math.max(12, style.top), window.innerHeight - TH - 12);
  } else {
    style = { ...style, top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  }

  return (
    <>
      <div ref={overlayRef} className="fixed inset-0 z-[10000] pointer-events-none">
        <div className="absolute inset-0 bg-[#18181B]/60 backdrop-blur-[1px] pointer-events-auto" />
        {pos && (
          <div
            className="absolute rounded-lg pointer-events-none transition-all duration-300"
            style={{
              top: pos.top - 6, left: pos.left - 6,
              width: pos.width + 12, height: pos.height + 12,
              boxShadow: "0 0 0 9999px rgba(3,7,17,0.65), 0 0 0 3px #2563FF, 0 0 28px rgba(79,140,255,0.6)",
              borderRadius: 12,
            }}
          />
        )}
      </div>
      <div className="aria-card p-5 fade-in pointer-events-auto" style={style} data-testid="tour-popover">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--aria-primary)] font-semibold">
            Tour · {idx + 1} / {TOUR_STEPS.length}
          </div>
          <button onClick={() => finish(true)} data-testid="tour-close"
            className="text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]">
            <X size={16} />
          </button>
        </div>
        <h3 className="font-serif text-xl mb-2">{step.title}</h3>
        <p className="text-sm text-[var(--aria-text-muted)] leading-relaxed mb-4">{step.body}</p>
        <div className="flex items-center justify-between">
          <button onClick={() => finish(true)} data-testid="tour-skip" className="text-xs text-[var(--aria-text-muted)] hover:underline">
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            {idx > 0 && (
              <button onClick={prev} data-testid="tour-prev"
                className="px-3 py-1.5 text-sm rounded-md border border-[var(--aria-border)] hover:bg-slate-50 dark:hover:bg-white/5 flex items-center gap-1">
                <ArrowLeft size={14} /> Back
              </button>
            )}
            <button onClick={next} data-testid="tour-next"
              className="px-3 py-1.5 text-sm rounded-md bg-[var(--aria-primary)] text-white hover:bg-[var(--aria-primary-hover)] flex items-center gap-1">
              {idx >= TOUR_STEPS.length - 1 ? "Finish" : "Next"} <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
